import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AdminRoute from './components/layout/AdminRoute';
import Layout from './components/layout/Layout';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Zap } from 'lucide-react';

// Lazy load pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BusinessPage = lazy(() => import('./pages/BusinessPage'));
const BusinessSetupPage = lazy(() => import('./pages/BusinessSetupPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const InvoiceFormPage = lazy(() => import('./pages/InvoiceFormPage'));
const InvoiceSummaryPage = lazy(() => import('./pages/InvoiceSummaryPage'));
const ReviewInvoicePage = lazy(() => import('./pages/ReviewInvoicePage'));
const YourInvoicePage = lazy(() => import('./pages/YourInvoicePage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const BusinessDetailsPage = lazy(() => import('./pages/BusinessDetailsPage'));
const ClientDetailsPage = lazy(() => import('./pages/ClientDetailsPage'));
const ShippingDetailsPage = lazy(() => import('./pages/ShippingDetailsPage'));
const ItemDetailsPage = lazy(() => import('./pages/ItemDetailsPage'));
const PaymentAccountsPage = lazy(() => import('./pages/PaymentAccountsPage'));
const NewInvoice = lazy(() => import('./pages/NewInvoice'));
const InvoiceJourneyPage = lazy(() => import('./pages/InvoiceJourneyPage'));
const ViewInvoicePage = lazy(() => import('./pages/ViewInvoicePage'));
const OcrPremiumPage = lazy(() => import('./pages/OcrPremiumPage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));

const LoadingFallback = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    height="100vh"
    gap={3}
    sx={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #eef2f7 50%, #fdf6e3 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute', width: 320, height: 320, borderRadius: '50%',
        top: -120, right: -80,
        background: 'radial-gradient(circle, rgba(234, 179, 8, 0.35), transparent 70%)',
        filter: 'blur(50px)',
        animation: 'invokta-orb-drift 12s ease-in-out infinite',
      }}
    />
    <Box
      sx={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        bottom: -120, left: -90,
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.35), transparent 70%)',
        filter: 'blur(50px)',
        animation: 'invokta-orb-drift 16s ease-in-out infinite',
        animationDelay: '-5s',
      }}
    />
    <Box
      className="animate-pop"
      sx={{
        width: 72, height: 72, borderRadius: 3,
        background: 'linear-gradient(135deg, #0b2b4a 0%, #312e81 100%)',
        color: '#eab308',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 18px 42px -12px rgba(11, 43, 74, 0.5)',
        animation: 'invokta-float 3.4s ease-in-out infinite',
      }}
    >
      <Zap
        size={34}
        style={{ animation: 'invokta-spin-slow 9s linear infinite' }}
      />
    </Box>
    <Typography
      variant="h6"
      className="text-gradient-brand"
      sx={{ fontWeight: 800, letterSpacing: '-0.01em', fontSize: '1.35rem' }}
    >
      Invokta
    </Typography>
    <Box
      sx={{
        width: 160,
        height: 4,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'rgba(11, 43, 74, 0.08)',
      }}
    >
      <Box
        className="animate-gradient"
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          background: 'linear-gradient(90deg, #0b2b4a 0%, #eab308 50%, #0b2b4a 100%)',
        }}
      />
    </Box>
  </Box>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
            <Route path="/oauth-callback" element={<OAuthCallback />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/support" element={<SupportPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/invoices/view/:invoiceNumber" element={<ViewInvoicePage />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/business" element={<BusinessPage />} />
                <Route path="/business-setup" element={<BusinessSetupPage />} />
                <Route path="/:businessName" element={<Dashboard />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/invoices/new" element={<InvoiceFormPage />} />
                <Route path="/invoices/new-invoice" element={<NewInvoice />} />
                <Route path="/invoices/journey" element={<InvoiceJourneyPage />} />
                <Route path="/invoices/summary/new" element={<InvoiceSummaryPage />} />
                <Route path="/invoices/summary/:id" element={<InvoiceSummaryPage />} />
                <Route path="/invoices/review" element={<ReviewInvoicePage />} />
                <Route path="/invoices/your-invoice" element={<YourInvoicePage />} />
                <Route path="/invoices/edit/:invoiceNumber" element={<InvoiceFormPage />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/business-details" element={<BusinessDetailsPage />} />
                <Route path="/client-details" element={<ClientDetailsPage />} />
                <Route path="/shipping-details" element={<ShippingDetailsPage />} />
                <Route path="/item-details" element={<ItemDetailsPage />} />
                <Route path="/payment-accounts" element={<PaymentAccountsPage />} />
                <Route path="/upgrade/ocr" element={<OcrPremiumPage />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </LocalizationProvider>
  </QueryClientProvider>
);

export default App;
